import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { PairAddress } from '@root/entities/pair'
import { TradeRoute } from '@root/features/routing'

import type { BestDeal } from '@root/entities/profile'
import type { ClassNameProps } from '@root/shared/react'

type BestDetailEntryProps = {
  bestDeal: BestDeal.BestDeal
} & ClassNameProps

export const BestDetailEntry: React.FC<BestDetailEntryProps> = ({
  bestDeal,
  className,
}) => {
  const { t } = useTranslation()
  const nativeCurrency = CurrencyModel.nativeOf(bestDeal.currency.network)

  return (
    <Root className={className}>
      <Header>
        <CurrencyContainer>
          <CurrencyIcon size={20} currency={bestDeal.currency} />
          <CurrencyName
            to={TradeRoute.build({
              pairAddress: PairAddress(bestDeal.currency.address),
              network: bestDeal.currency.network,
            })}
          >
            {bestDeal.currency.ticker}
          </CurrencyName>
        </CurrencyContainer>
        <Date>{format(bestDeal.date, 'dd.MM.yyyy')}</Date>
      </Header>
      <HeaderSeparator />
      <PricesContainer>
        <Price>
          <PriceLabel>{t('profile.best_deal.entry_price')}</PriceLabel>
          <PriceNativeValue>
            <Numeric
              value={bestDeal.entryPriceNative}
              preset="currency-amount-short"
              currency={nativeCurrency.ticker}
            />
          </PriceNativeValue>
          <PriceUsdValue>
            <Numeric
              value={bestDeal.entryPriceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </PriceUsdValue>
        </Price>
        <PriceSeparator />
        <Price>
          <PriceLabel>{t('profile.best_deal.close_price')}</PriceLabel>
          <PriceNativeValue>
            <Numeric
              value={bestDeal.closePriceNative}
              preset="currency-amount-short"
              currency={nativeCurrency.ticker}
            />
          </PriceNativeValue>
          <PriceUsdValue>
            <Numeric
              value={bestDeal.closePriceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </PriceUsdValue>
        </Price>
      </PricesContainer>
    </Root>
  )
}

const Root = styled.div`
  width: 176px;
  height: 160px;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--backgroundA-3);
  backdrop-filter: blur(20px);
`

const Header = styled.div`
  padding: 8px 10px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  justify-content: space-between;
`

const HeaderSeparator = styled.div`
  width: 100%;
  height: 1px;
  background: var(--backgroundA-3);
  margin-bottom: 9px;
`

const CurrencyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const CurrencyName = styled(Link)`
  color: var(--text-12);
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
`

const Date = styled.div`
  color: var(--textA-7);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  grid-column: 3;
`

const PricesContainer = styled.div`
  padding: 0 10px;
  padding-bottom: 9px;
`

const Price = styled.div`
  display: grid;
  grid-template-rows: repeat(3, auto);
  gap: 2px;
`

const PriceLabel = styled.div`
  color: var(--text-11);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const PriceNativeValue = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`

const PriceUsdValue = styled.div`
  color: var(--textA-9);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const PriceSeparator = styled.div`
  width: 100%;
  height: 1px;
  background: var(--backgroundA-3);
  margin: 8px 0;
`
